// @react
import React, { ReactElement, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @libraries
import { makeStyles } from '@material-ui/core/styles'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
// @commons
import strings from 'lib/constants/strings'
import queryString from 'query-string'
import colors from '@bit/atd.web.colors/dist/colors'
// @components
import Followers from './FollowedUsers'
import Following from './FollowingUsers'
import Groups from './Groups'
import RecommendedUsers from './RecommendedUsers'
// @graphql
import { useQuery } from '@apollo/client'
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
import { GET_USER } from 'graphql/queries/user.query'
import Loader from '__COMPONENTS_TO_MIGRATE/Loaders/Base'

/**
 * 5.0.6 Profile - Network
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=2507%3A7294
 */

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {<div style={{ marginTop: '24px' }}>{children}</div>}
    </div>
  )
}

const a11yProps = (index: number) => ({
  id: `tab-${index}`,
  'aria-controls': `tabpanel-${index}`,
  value: index,
})

const useStyles = makeStyles({
  tabHeader: {
    borderBottom: `1px solid ${colors.gray300}`,
    marginBottom: '32px',
    '& .MuiTab-root': {
      fontSize: '14px',
      lineHeight: '14px',
    },
    '& .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      paddingBottom: '8px',
    },
    '& .MuiTab-wrapper > span:first-child': {
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      fontWeight: '600',
    },
    '& .MuiTab-wrapper > span + span': {
      border: '1px solid #DBD9D6',
      borderRadius: '12px',
      color: '#424140',
      fontSize: '14px',
      fontWeight: '500',
      padding: '4px 8px',
      marginLeft: '8px',
    },
  },
})

const tabIndexes = {
  FOLLOWING: 0,
  FOLLOWERS: 1,
  GROUPS: 2,
  RECOMMENDED: 3,
}

const NetworkTabs = (): ReactElement => {
  const history = useHistory()
  const { tab } = queryString.parse(history.location.search)

  /******************************************
   * STATE
   ******************************************/

  const [value, setValue] = useState<number>(Number(tab) || 0)
  const [followingCount, setFollowingCount] = useState<number>(0)
  const [followersCount, setFollowersCount] = useState<number>(0)
  const [groupsCount, setGroupsCount] = useState<number>(0)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const { data } = useQuery<GetUserQuery, GetUserQueryVariables>(GET_USER, {
    fetchPolicy: 'cache-first',
  })

  const showGroupsTab = data?.user?.membership.isMember

  /******************************************
   * FUNCTIONS
   ******************************************/

  const handleChange = (
    _: React.ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    history.push({ search: queryString.stringify({ tab: newValue }) })
    setValue(newValue)
  }

  /******************************************
   * RENDER
   ******************************************/

  return (
    <div>
      <Tabs
        className={classes.tabHeader}
        value={value}
        onChange={handleChange}
        variant="scrollable"
        aria-label="Networks"
      >
        <Tab
          label={
            <>
              <span>{strings.PROFILE.NETWORK.FOLLOWING}</span>
              <span>{followingCount}</span>
            </>
          }
          {...a11yProps(tabIndexes.FOLLOWING)}
        />
        <Tab
          label={
            <>
              <span>{strings.PROFILE.NETWORK.FOLLOWERS}</span>
              <span>{followersCount}</span>
            </>
          }
          {...a11yProps(tabIndexes.FOLLOWERS)}
        />
        {showGroupsTab && (
          <Tab
            label={
              <>
                <span>{strings.PROFILE.NETWORK.GROUPS}</span>
                <span>{groupsCount}</span>
              </>
            }
            {...a11yProps(tabIndexes.GROUPS)}
          />
        )}
        <Tab
          label={<span>{strings.PROFILE.NETWORK.SUGGESTED_USERS_TITLE}</span>}
          {...a11yProps(tabIndexes.RECOMMENDED)}
        />
      </Tabs>

      <TabPanel value={value} index={tabIndexes.FOLLOWING}>
        <Following setCount={setFollowingCount} />
      </TabPanel>

      <TabPanel value={value} index={tabIndexes.FOLLOWERS}>
        <Followers setCount={setFollowersCount} />
      </TabPanel>

      {showGroupsTab && (
        <TabPanel value={value} index={tabIndexes.GROUPS}>
          <Groups setCount={setGroupsCount} />
        </TabPanel>
      )}
      <TabPanel value={value} index={tabIndexes.RECOMMENDED}>
        <RecommendedUsers />
      </TabPanel>
    </div>
  )
}

export default NetworkTabs
